import { CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideMousePointerClick, lucidePlus } from '@ng-icons/lucide';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { Cartographie } from '../../cartographie/models/cartographie.model';

import { ListItem } from '../../shared/components/list-item/list-item.component';
import { SidepanelService } from '../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../shared/components/toastr/toastr-success.component';
import { TrierParPipe } from '../../shared/pipes/trier-par.pipe';
import { ProductionStore } from '../référentiel-métier-ressources-professionnelles/store/production.store';
import { Aptitude } from './processus-aptitude/models/aptitude.model';
import { ProcessusAptitudeComponent } from './processus-aptitude/processus-aptitude.component';
import { AptitudeStore } from './processus-aptitude/store/aptitude.store';
import { IndicateurPerformanceStore } from './processus-indicateur-performance/store/indicateur-performance.store';
import { FormIndicesComponent } from './processus-indice-resultats/form/form-indices/form-indices.component';
import { IndiceRésultatsStore } from './processus-indice-resultats/store/indice-résultats.store';
import { FormPhasesComponent } from './processus-phases/forms/form-phases/form-phases.component';
import { ItemPhaseComponent } from './processus-phases/item-phase/item-phase.component';
import { Phase } from './processus-phases/models/phase.model';
import { PhaseStore } from './processus-phases/store/phase.store';
import { ProductionsFormComponent } from './processus-production/forms/productions-form/productions-form.component';
import { HlmResizableImports } from '@spartan-ng/helm/resizable';

@Component({
  selector: 'app-referentiel-metier-processus',
  imports: [
    HlmButtonImports,
    HlmDropdownMenuImports,
    RouterModule,
    HlmCardImports,
    HlmIconImports,
    TrierParPipe,
    CdkDropListGroup,
    CdkDropList,
    ListItem,
    ItemPhaseComponent,
    ChipComponent,
    EmptyStateComponent,
    HlmSeparatorImports,
    ProcessusAptitudeComponent,
    HlmResizableImports,
  ],
  templateUrl: './référentiel-métier-processus.component.html',
  providers: [
    provideIcons({
      lucidePlus,
      lucideMousePointerClick,
    }),
  ],
})
export class RéférentielMétierProcessusComponent {
  readonly #phaseStore = inject(PhaseStore);
  readonly #indiceStore = inject(IndiceRésultatsStore);
  readonly #indicateurStore = inject(IndicateurPerformanceStore);
  readonly #productionStore = inject(ProductionStore);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #canEditService = inject(CanEditService);
  readonly #estVerrouilleService = inject(EstVerrouilleService);

  readonly cartographie = input.required<Cartographie>();
  readonly roles = input.required<string[]>();
  readonly idAptitude = input<string>();

  readonly referentiel = computed(() => this.#referentielStore.selectedEntity());
  readonly phases = computed(() => this.#phaseStore.entities());
  readonly aptitudes = computed(() => this.phases().flatMap((x) => x.aptitudes));

  readonly selectedAptitude = signal<Aptitude | undefined>(undefined);

  peutModifier = computed(() =>
    this.#canEditService.check(
      this.referentiel()?.equipeResponsable,
      this.roles(),
      LivrablePrincipal.REFERENTIEL_METIER,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  readonly estVerrouille = computed(() =>
    this.#estVerrouilleService.check(LivrablePrincipal.REFERENTIEL_METIER, this.cartographie(), this.referentiel()),
  );

  constructor() {
    effect(() => {
      const referentiel = this.referentiel();
      if (referentiel) {
        this.#phaseStore.charger(referentiel.id);
        this.#indicateurStore.charger(referentiel.id);
        this.#indiceStore.charger(referentiel.id);
      }
    });

    effect(() => {
      const idAptitude = this.idAptitude();
      const aptitudes = this.phases().flatMap((x) => x.aptitudes);

      if (idAptitude) {
        const aptitude = aptitudes.find((x) => x.id === idAptitude);
        this.selectedAptitude.set(aptitude);
        this.#productionStore.chargerProductions({ productions: aptitude?.productions ?? [] });
      }
    });
  }

  ajouterPhase() {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une nouvelle phase',
      FormPhasesComponent,
      'Créez une ou plusieurs phases, et ajoutez-les les unes sous les autres.',
      {
        referentiel: this.referentiel(),
      },
    );
    this.#sidepanelService.open();
  }

  dropPhase(event: CdkDragDrop<Phase[]>) {
    const referentiel = this.referentiel();
    if (!referentiel) return;

    if (event.previousIndex === event.currentIndex) return;

    const toastrId = uuidv7();

    moveItemInArray(this.phases(), event.previousIndex, event.currentIndex);

    const idReferentiel = referentiel.id;

    const phase = event.item.data as Phase;
    phase.indice = event.currentIndex;
    this.#phaseStore.modifier({
      idPhase: phase.id,
      data: {
        indice: event.currentIndex,
        nom: phase.nom!,
        idReferentiel,
      },
      next: () => {
        this.#phaseStore.charger(idReferentiel);
        toast.custom(ToastrSuccessComponent, {
          componentProps: { message: 'Réordonnancement des phases réussi', id: toastrId },
          id: toastrId,
        });
      },
    });
  }

  createProduction(aptitude: Aptitude | undefined) {
    if (!aptitude) return;

    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une nouvelle production',
      ProductionsFormComponent,
      `Les productions sont des ensembles de ressources professionnelles regroupées sous un même libellé, et sont rattachées à une aptitude.<br /><span class="paragraph-medium-semibold">L'aptitude parente est ${aptitude.nom}</span>.`,
      { idAptitude: aptitude.id },
    );
    this.#sidepanelService.open();
  }

  createIndiceResultat(aptitude: Aptitude | undefined): void {
    if (!aptitude) return;

    this.#sidepanelService.setComponentToDisplay(
      'Ajouter un nouvel indice de résultats',
      FormIndicesComponent,
      'Créez une ou plusieurs indices de résultats, et ajoutez-les les uns sous les autres.',
      {
        estCreation: true,
        referentiel: this.referentiel(),
        cartographie: this.cartographie(),
        aptitude,
        phases: this.phases(),
      },
    );
    this.#sidepanelService.open();
  }
}
